import { colors } from '@globals/themes/defaultTheme'
import { desktopQuery, onHover, semiBoldFontWeight } from '@globals/themes/themeConstants'
import { useCurrentMember } from '@globals/useCurrentMember'
import useMemberProfileRoute from '@globals/useMemberProfileRoute'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MemberProfilePreviewCard, {
  MemberProfilePreviewCardSkeleton,
} from '@shared/memberProfilePreview/MemberProfilePreviewCard'
import {
  getMentionPreviewRegionId,
  isPreviewableTenantMember,
  isUserFacingPreviewRoute,
} from '@shared/memberProfilePreview/memberProfilePreviewUtils'
import React, { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router'
import HtmlContent from '../HtmlContent'
import MentionsPopper from './MentionsPopper'
import { useHtmlContentWithMentions } from './useHtmlContentWithMentions'

export const HtmlContentStyle: SxProps = {
  overflowWrap: 'anywhere',
  '& span[data-type="mention"]': {
    fontWeight: semiBoldFontWeight,
    color: colors.bluePrimary,
    [onHover]: {
      cursor: 'pointer',
    },
  },
  // Deleted mentions keep the mention styling (incl. blue color) but drop the
  // pointer affordance since they no longer navigate anywhere. They are the only
  // mentions without a button role (unverified/loading ones stay interactive and
  // keep it), so target the absence of role to remove the pointer cursor.
  '& span[data-type="mention"]:not([role])': {
    cursor: 'default',
    [onHover]: {
      cursor: 'default',
    },
  },
  '& > p': {
    marginBlockStart: 0,
  },
  '& > p:last-child': {
    marginBlockEnd: 0,
  },
}

/**
 * The tooltip is a bare positioning shell: each preview brings its own surface, so the
 * default padding, background, width cap, and shadow are all stripped.
 */
const previewTooltipSx: SxProps = {
  p: 0,
  bgcolor: 'transparent',
  maxWidth: 'none',
  boxShadow: 'none',
}

type HtmlContentWithMentionsProps = {
  htmlContent: string
  height?: number | string
  maxHeight?: number | string
}

const HtmlContentWithMentions: React.FC<HtmlContentWithMentionsProps> = ({
  htmlContent,
  height = '100%',
  maxHeight = '100%',
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isDesktop = useMediaQuery(desktopQuery)
  const { member: currentMember } = useCurrentMember()
  const useRichPreview = isDesktop && isUserFacingPreviewRoute(pathname) && Boolean(currentMember)
  const getMemberProfileRoute = useMemberProfileRoute()
  const onNavigate = useCallback(
    (id: string) => navigate(getMemberProfileRoute(id)),
    [navigate, getMemberProfileRoute],
  )

  const {
    containerRef,
    anchorEl,
    tenantMemberId,
    open,
    closePreview,
    member,
    isFetched,
    deletedMentionIds,
  } = useHtmlContentWithMentions({
    htmlContent,
    onNavigate,
    selfMemberId: currentMember?.id,
    richPreview: useRichPreview,
  })

  const content = (
    <Box ref={containerRef} sx={{ height, overflow: 'auto', maxHeight }}>
      <HtmlContent htmlContent={htmlContent} sx={HtmlContentStyle} />
    </Box>
  )

  // Rendered from tenantMemberId (not `open`) so the content stays mounted through
  // the Tooltip's exit transition; `open` still controls show/hide. A null title is
  // treated by MUI as "no tooltip", same as an empty one.
  const renderPreviewContent = (): React.ReactNode => {
    if (!tenantMemberId || deletedMentionIds.has(tenantMemberId)) {
      return null
    }
    if (!useRichPreview) {
      return <MentionsPopper tenantMemberId={tenantMemberId} member={member} />
    }
    if (member && isPreviewableTenantMember(member)) {
      return (
        <MemberProfilePreviewCard
          member={member}
          onClose={closePreview}
          onNavigate={() => {
            closePreview()
            onNavigate(tenantMemberId)
          }}
          previewRegionId={getMentionPreviewRegionId(tenantMemberId)}
        />
      )
    }
    if (!isFetched) {
      return <MemberProfilePreviewCardSkeleton />
    }
    return null
  }

  return (
    <Tooltip
      open={open && isDesktop}
      onClose={closePreview}
      leaveDelay={120}
      disableTouchListener
      placement="top-start"
      slotProps={{
        tooltip: { sx: previewTooltipSx },
        popper: {
          anchorEl: anchorEl ?? undefined,
          // Same layer the toaster sits on, keeping the preview above dialogs.
          sx: theme => ({ zIndex: theme.zIndex.snackbar }),
        },
      }}
      title={renderPreviewContent()}>
      {content}
    </Tooltip>
  )
}

export default HtmlContentWithMentions
